import initSqlJs from 'sql.js';
import sqlWasmUrl from 'sql.js/dist/sql-wasm.wasm?url';
import { APP_VERSION, DB_IDB_KEY } from '../constants';
import { nowMs } from '../lib/time';
import { idbDelete, idbGet, idbSet } from './idb';
import { MIGRATIONS, SCHEMA_VERSION } from './schema';

type SqlDatabase = any;
type SqlRuntime = any;

let SQL: SqlRuntime | null = null;
let db: SqlDatabase | null = null;
let dbInitError: string | null = null;

async function getSql(): Promise<SqlRuntime> {
  if (SQL) return SQL;
  SQL = await initSqlJs({
    locateFile: (file: string) => (file.endsWith('.wasm') ? sqlWasmUrl : file)
  });
  return SQL;
}

function runMigrations(database: SqlDatabase): void {
  // Ensure migration metadata table exists before reading schema version.
  database.run(`
    CREATE TABLE IF NOT EXISTS meta (
      key TEXT PRIMARY KEY,
      value TEXT NOT NULL
    );
  `);
  const currentRaw = getMetaNumber(database, 'schema_version');
  const current = Number.isFinite(currentRaw) && currentRaw >= 0 ? currentRaw : 0;
  for (let i = current; i < SCHEMA_VERSION; i += 1) {
    runMigrationBlock(database, MIGRATIONS[i]);
  }
  setMeta(database, 'schema_version', String(SCHEMA_VERSION));
  if (!getMeta(database, 'install_ts_ms')) {
    setMeta(database, 'install_ts_ms', String(nowMs()));
  }
  setMeta(database, 'app_version', APP_VERSION || 'dev');
}

function runMigrationBlock(database: SqlDatabase, block: string): void {
  const statements = block
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean);
  for (const stmt of statements) {
    try {
      database.run(stmt);
    } catch (error) {
      const msg = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
      // Keep upgrades idempotent if a previous run partially applied an ALTER TABLE.
      if (msg.includes('duplicate column name')) continue;
      throw error;
    }
  }
}

function getMeta(database: SqlDatabase, key: string): string | null {
  const stmt = database.prepare('SELECT value FROM meta WHERE key = ? LIMIT 1');
  stmt.bind([key]);
  if (!stmt.step()) {
    stmt.free();
    return null;
  }
  const row = stmt.getAsObject() as { value?: unknown };
  stmt.free();
  return row.value == null ? null : String(row.value);
}

function getMetaNumber(database: SqlDatabase, key: string): number {
  const v = getMeta(database, key);
  return v ? Number(v) : 0;
}

function setMeta(database: SqlDatabase, key: string, value: string): void {
  const safeValue = value ?? '';
  const stmt = database.prepare('INSERT OR REPLACE INTO meta(key, value) VALUES (?, COALESCE(?, \'\'))');
  stmt.run([key, safeValue]);
  stmt.free();
}

export async function getDb(): Promise<SqlDatabase> {
  if (db) return db;
  try {
    const sql = await getSql();
    const existing = await idbGet(DB_IDB_KEY);
    try {
      db = existing ? new sql.Database(existing) : new sql.Database();
    } catch {
      // Persisted bytes are corrupt; reset local DB.
      await idbDelete(DB_IDB_KEY);
      db = new sql.Database();
    }
    runMigrations(db);
    await persistDb();
    return db;
  } catch (error) {
    dbInitError = error instanceof Error ? error.message : 'Storage unavailable';
    throw error;
  }
}

export async function persistDb(): Promise<void> {
  if (!db) return;
  const data = db.export();
  await idbSet(DB_IDB_KEY, data);
}

export async function exportDbSnapshot(): Promise<Uint8Array> {
  const database = await getDb();
  return database.export();
}

export async function importDbSnapshot(snapshot: Uint8Array): Promise<void> {
  const sql = await getSql();
  const nextDb = new sql.Database(snapshot);
  runMigrations(nextDb);
  const migrated = nextDb.export();
  if (typeof nextDb.close === 'function') nextDb.close();
  if (db && typeof db.close === 'function') db.close();
  db = new sql.Database(migrated);
  runMigrations(db);
  await persistDb();
}

export function getDbInitError(): string | null {
  return dbInitError;
}
